import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { ChatListPage, ChatListRow, ChatTopicKind } from '@projoint/shared';

/**
 * The Chat list cache (`['me','chats']`, an infinite query of
 * `GET /api/me/chats` pages) and the pure patches that keep it live without a
 * refetch: a new message moves its row to the top with the new preview and
 * bumps the badge; pin / mute / read update the row in place.  Pure, so the
 * ordering rules are unit-tested; the realtime layer and the mutations call
 * them through `patchChatList`.
 */
export const CHAT_LIST_KEY = ['me', 'chats'] as const;

export type ChatListData = InfiniteData<ChatListPage, unknown>;

const rowKey = (r: Pick<ChatListRow, 'kind' | 'id'>) => `${r.kind}:${r.id}`;

/** Server order: pinned (newest pin first), then newest activity. */
export function compareRows(a: ChatListRow, b: ChatListRow): number {
  if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
  if (a.isPinned && b.isPinned && a.pinnedAt !== b.pinnedAt) {
    return (b.pinnedAt ?? '').localeCompare(a.pinnedAt ?? '');
  }
  const t = Date.parse(b.activityAt) - Date.parse(a.activityAt);
  if (t !== 0) return t;
  if (a.kind !== b.kind) return a.kind < b.kind ? 1 : -1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/** Every loaded row, de-duplicated, in list order. */
export function flattenChatList(data: ChatListData | undefined): ChatListRow[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: ChatListRow[] = [];
  for (const page of data.pages) {
    for (const row of page.items) {
      const k = rowKey(row);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(row);
    }
  }
  return out;
}

/**
 * Apply `fn` to the loaded rows and re-sort; the page sizes are kept so each
 * page's server cursor stays meaningful.  Returns the same object when `fn`
 * changed nothing.
 */
export function patchRows(
  data: ChatListData,
  fn: (rows: ChatListRow[]) => ChatListRow[],
): ChatListData {
  const before = flattenChatList(data);
  const after = fn(before);
  if (after === before) return data;
  const sorted = [...after].sort(compareRows);
  let i = 0;
  const pages = data.pages.map((page, idx) => {
    const size = idx === data.pages.length - 1 ? sorted.length - i : page.items.length;
    const items = sorted.slice(i, i + Math.max(0, size));
    i += items.length;
    return { ...page, items };
  });
  return { ...data, pages };
}

export interface IncomingChatMessage {
  kind: 'team' | 'dm';
  /** team id / dm thread id */
  chatId: string;
  topicId?: string | null | undefined;
  topicName?: string | null | undefined;
  topicKind?: ChatTopicKind | null | undefined;
  messageId: string;
  authorId: string;
  authorName: string | null;
  preview: string | null;
  createdAt: string;
  mentionsMe: boolean;
  /** The conversation is on screen: it reads the message itself — no badge. */
  active: boolean;
}

/** A new message: newest preview, row to the top, badge +1 (unless mine / muted channel / on screen). */
export function applyIncomingMessage(
  data: ChatListData,
  msg: IncomingChatMessage,
  meId: string | undefined,
): { data: ChatListData; found: boolean } {
  let found = false;
  const next = patchRows(data, (rows) => {
    const idx = rows.findIndex((r) => r.kind === msg.kind && r.id === msg.chatId);
    if (idx < 0) return rows;
    found = true;
    const row = rows[idx]!;
    if (row.lastMessage?.id === msg.messageId) return rows; // replay
    const newer = !row.lastMessage || Date.parse(msg.createdAt) >= Date.parse(row.lastMessage.at);
    const fromOther = !!meId && msg.authorId !== meId;
    const mutedTopic = msg.kind === 'team' && !!msg.topicId && row.mutedTopicIds.includes(msg.topicId);
    const counts = fromOther && !msg.active;
    const updated: ChatListRow = {
      ...row,
      lastMessage: newer
        ? {
            id: msg.messageId,
            preview: msg.preview,
            authorId: msg.authorId,
            authorName: msg.authorName,
            at: msg.createdAt,
            topicId: msg.kind === 'team' ? (msg.topicId ?? null) : null,
            topicName: msg.kind === 'team' ? (msg.topicName ?? null) : null,
            topicKind: msg.kind === 'team' ? (msg.topicKind ?? null) : null,
          }
        : row.lastMessage,
      activityAt: newer ? msg.createdAt : row.activityAt,
      unreadCount: row.unreadCount + (counts && !mutedTopic ? 1 : 0),
      hasMention: row.hasMention || (counts && msg.mentionsMe),
    };
    const copy = [...rows];
    copy[idx] = updated;
    return copy;
  });
  return { data: next, found };
}

/** Replace one row's fields (pin / mute / read). */
export function updateRow(
  data: ChatListData,
  kind: ChatListRow['kind'],
  id: string,
  patch: (row: ChatListRow) => Partial<ChatListRow>,
): ChatListData {
  return patchRows(data, (rows) => {
    const idx = rows.findIndex((r) => r.kind === kind && r.id === id);
    if (idx < 0) return rows;
    const copy = [...rows];
    copy[idx] = { ...rows[idx]!, ...patch(rows[idx]!) };
    return copy;
  });
}

export function patchChatList(qc: QueryClient, fn: (data: ChatListData) => ChatListData): void {
  qc.setQueryData<ChatListData>(CHAT_LIST_KEY, (old) => (old ? fn(old) : old));
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Reconcile the list with the server once things settle — many events in a
 * burst (a busy channel, several devices) make ONE refetch, never a storm.
 */
export function scheduleChatListRefresh(qc: QueryClient, delayMs = 1500): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    void qc.invalidateQueries({ queryKey: CHAT_LIST_KEY });
  }, delayMs);
}
